import { useEffect, useState } from "react";

/** The viewer's local calendar day, e.g. "2026-07-17". */
function localDayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/**
 * A coarse clock signal that changes when the viewer's local calendar day rolls
 * over — the boundary every relative date preset (today / this-week / mtd / ytd)
 * resolves against. The scope hooks fold it into their memo key so a dashboard
 * left open past midnight re-resolves "Today" instead of silently counting
 * yesterday behind a chip that still reads "Today" (B8).
 *
 * Updates on a coarse 60s foreground interval (the bounds are day-granular, so
 * a minute of slop at the boundary is invisible) AND on `visibilitychange`, so a
 * laptop that slept across midnight rolls over the moment its tab is refocused.
 */
export function useDateBoundaryKey(): string {
  const [dayKey, setDayKey] = useState(localDayKey);
  useEffect(() => {
    const check = () => {
      const next = localDayKey();
      setDayKey((prev) => (prev === next ? prev : next));
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisibility);
    const id = setInterval(check, 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(id);
    };
  }, []);
  return dayKey;
}

/**
 * The same signal one rung finer: a counter that ticks every minute, for the
 * surfaces whose text is minute-granular. An update deadline reads "19 minutes
 * overdue" and its status re-buckets On track → Update soon → Stale as the window
 * elapses, and both are derived at render from the last posted Update — so
 * without a tick a widget left open goes stale against a clock the viewer can
 * see running, and only a refetch would correct it.
 *
 * Read it as a memo/render key (`void useMinuteKey()` where nothing memoizes);
 * the value itself carries no meaning beyond "a minute passed". Foreground
 * only, and it re-checks on `visibilitychange` so a slept laptop catches up the
 * moment its tab is refocused rather than up to a minute later.
 */
export function useMinuteKey(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((prev) => prev + 1);
    const onVisibility = () => {
      if (document.visibilityState === "visible") bump();
    };
    document.addEventListener("visibilitychange", onVisibility);
    const id = setInterval(bump, 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(id);
    };
  }, []);
  return tick;
}
